import React, {
  ChangeEvent,
  DetailedHTMLProps,
  InputHTMLAttributes,
  KeyboardEvent,
  useId,
  useMemo,
  useState,
} from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies
import cn from 'classnames';

import eyeOpen from 'assets/images/eye-open.svg';
import eye from 'assets/images/eye.svg';

import './InputForm.scss';

type DefaultInputPropsType = DetailedHTMLProps<
  InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

type InputFormProps = DefaultInputPropsType & {
  onChangeText?: (value: string) => void;
  onEnter?: () => void;
  error?: string;
  spanClassName?: string;
  label?: string | React.ReactNode;
  subLabel?: string;
};

export const InputForm: React.FC<InputFormProps> = props => {
  const {
    type,
    onChange,
    onChangeText,
    onKeyDown,
    onEnter,
    error,
    className,
    spanClassName,
    label,
    subLabel,
    ...rest
  } = props;
  const [isShowPassword, setIsShowPassword] = useState<boolean>(false);
  const id = useId();
  const onChangeCallback = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) onChange(e);
    if (onChangeText) onChangeText(e.currentTarget.value);
  };
  const onKeyPressCallback = (e: KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) onKeyDown(e);

    if (onEnter) {
      if (e.key === 'Enter') onEnter();
    }
  };
  const finalClassname = error ? 'InputForm__Input InputForm__Error' : 'InputForm__Input';

  const conditionShowPassword = useMemo(() => {
    if (type === 'password' && isShowPassword) return 'text';

    return 'password';
  }, [isShowPassword]);
  const onChangeShowPassword = () => {
    setIsShowPassword(prev => !prev);
  };

  return (
    <div className={cn('InputForm', className)}>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      {label && (
        <label className="InputForm__Label" htmlFor={`${id}-input`}>
          {label} {subLabel && <small>{subLabel}</small>}
        </label>
      )}
      <input
        type={type === 'password' ? conditionShowPassword : type}
        onChange={onChangeCallback}
        onKeyDown={onKeyPressCallback}
        className={finalClassname}
        id={`${id}-input`}
        aria-describedby={`${id}-error-message`}
        {...rest}
      />
      {type === 'password' && (
        <img
          src={isShowPassword ? eyeOpen : eye}
          alt={isShowPassword ? 'hide password' : 'show password'}
          onClick={onChangeShowPassword}
          className="InputForm__Icon"
        />
      )}
      {error && (
        <span aria-live="polite" className="TextError" id={`${id}-error-message`}>
          {error}
        </span>
      )}
    </div>
  );
};
